import { 
    TitleLarge,
    TitleMedium,
    BodyXLMedium,
    BodyRegular
} from '@/components/typography';
import { PrimaryButton } from '@/components/buttons';
import { GenericCard } from '@/components/cards';
import { Accordion, TextBadge } from '@/components/elements';
// Ico structure images
import HeroImage from '@/assets/img/ui/28.png';
// Cards images
import IconNFT from '@/assets/img/ui/5.png';
import IconStartingPrice from '@/assets/img/ui/18.png';
import IconDuration from '@/assets/img/ui/10.png';
import IconStaking from '@/assets/img/ui/22.png';
import IconBrowse from '@/assets/img/ui/11.png';
import IconResale from '@/assets/img/ui/2.png';
import IconBuy from '@/assets/img/ui/13.png';
import IconEscrow from '@/assets/img/ui/16.png';
import IconTwoNFTs from '@/assets/img/ui/two-nfts-are-better-than-one.png';

export const Marketplace = () => {
    return (
        <div className="w-full bg-background">
            {/* Hero Section */}
            <section className="w-full flex justify-center px-4 py-16 lg:pt-44 relative">
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    {/* Star patterns */}
                </div>

                {/* Glow effects */}
                <div className="absolute left-1/4 top-1/4 w-96 h-96 opacity-60 bg-primary rounded-full blur-[247.13px] pointer-events-none" />
                <div className="absolute right-1/4 bottom-1/4 w-96 h-96 opacity-60 bg-cyan-400 rounded-full blur-[282.13px] pointer-events-none" />

                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12 relative z-10">
                    {/* Text content */}
                    <div className="w-full lg:flex-1 flex flex-col items-start gap-4 lg:gap-6 lg:max-w-[716px]">
                        <TextBadge className='w-max'>Marketplace</TextBadge>
                        <TitleLarge className="text-white max-w-full lg:max-w-[560px]">
                            Trade, Merge, and <span className="text-primary">Create Value</span>
                        </TitleLarge>
                        <BodyRegular className="text-white/80 lg:text-white/60 max-w-full lg:max-w-[492px]">
                            Buy and resell verified NFTs, fuse assets for upgrades, and benefit from a transparent resale floor (≥110% inside the platform).
                        </BodyRegular>
                        <PrimaryButton className="w-full lg:w-96">
                            Enter the Marketplace
                        </PrimaryButton>
                    </div>

                    {/* Image - desktop only */}
                    <div className="hidden lg:flex lg:flex-1 items-center justify-center relative">
                        <img 
                            className="absolute -top-60 right-0 w-[775.35px] rotate-[-3.95deg]" 
                            src={HeroImage}
                            alt="Marketplace Hero section image"
                        />
                    </div>
                </div>
            </section>

            {/* Key Confidence Bar Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-center gap-6 lg:gap-8">
                    <div className="flex flex-col items-center gap-4 lg:gap-6">
                        <TitleMedium className="text-white text-center">
                            <span className="text-primary">Key</span> Confidence Bar
                        </TitleMedium>
                        <BodyRegular className="text-white/80 lg:text-white/60 text-center max-w-[320px] lg:max-w-[738px]">
                            Stake for 12, 24, or 36 months to earn up to 50% APY plus loyalty and participation bonuses. Long-term stakers gain governance privileges, voting rights, and recognition within on-chain dashboards.
                        </BodyRegular>
                    </div>

                    {/* 2x2 grid */}
                    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col items-center gap-1">
                            <BodyXLMedium className="text-primary text-center">Verified rarity metrics</BodyXLMedium>
                            <BodyRegular>on-chain traits & rank</BodyRegular>
                        </div>
                        <div className="w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col items-center gap-1">
                            <BodyXLMedium className="text-primary text-center">Resale records</BodyXLMedium>
                            <BodyRegular>price history & owner count</BodyRegular>
                        </div>
                        <div className="w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col items-center gap-1">
                            <BodyXLMedium className="text-primary text-center">≥110% floor</BodyXLMedium>
                            <BodyRegular>enforced inside this marketplace</BodyRegular>
                        </div>
                        <div className="w-full p-4 bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-sm backdrop-blur-xl border border-white/10 flex flex-col items-center gap-1">
                            <BodyXLMedium className="text-primary text-center">Escrow & refunds</BodyXLMedium>
                            <BodyRegular>failed/canceled trades</BodyRegular>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Features Section */}
            <section className="w-full flex justify-center px-4 py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col gap-6 lg:gap-8">
                    <TitleMedium className="text-white">
                        Core <span className="text-primary">Features</span>
                    </TitleMedium>

                    <div className="w-full grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-6">
                        {/* Feature 1 */}
                        <div className="flex flex-col gap-4">
                            <BodyXLMedium className="text-primary">Rarity at a glance</BodyXLMedium>
                            <div className="flex flex-col gap-2">
                                <TextBadge textClassName="text-white">Trait breakdown</TextBadge>
                                <TextBadge textClassName="text-white">Rank</TextBadge>
                                <TextBadge textClassName="text-white">Last sale</TextBadge>
                                <TextBadge textClassName="text-white">Average resale</TextBadge>
                                <TextBadge textClassName="text-white">Owner timeline</TextBadge>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="flex flex-col gap-4">
                            <BodyXLMedium className="text-primary">Transparent floor</BodyXLMedium>
                            <div className="flex flex-col gap-2">
                                <TextBadge textClassName="text-white" className="text-center">Listings must be ≥110% of the original win price</TextBadge>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="flex flex-col gap-4">
                            <BodyXLMedium className="text-primary">Smart listing tools</BodyXLMedium>
                            <div className="flex flex-col gap-2">
                                <TextBadge textClassName="text-white">Request-to-Buy,</TextBadge>
                                <TextBadge textClassName="text-white">Timed listings</TextBadge>
                                <TextBadge textClassName="text-white">Reserve</TextBadge>
                                <TextBadge textClassName="text-white">Relist in one tap</TextBadge>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="flex flex-col gap-4">
                            <BodyXLMedium className="text-primary">Creator & treasury split</BodyXLMedium>
                            <div className="flex flex-col gap-2">
                                <TextBadge textClassName="text-white">Trait breakdown</TextBadge>
                                <TextBadge textClassName="text-white">Rank</TextBadge>
                                <TextBadge textClassName="text-white">Last sale</TextBadge>
                                <TextBadge textClassName="text-white">Average resale</TextBadge>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How Fusion Works Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-24 relative overflow-hidden">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col items-center gap-6 lg:gap-16 relative z-10">
                    <TitleMedium className="text-white text-center">
                        How Fusion <span className="text-primary">Works</span>
                    </TitleMedium>

                    {/* Steps container */}
                    <div className="w-full flex flex-col items-center gap-8 lg:gap-20 relative">
                        {/* Vector doodad - desktop only */}
                        <svg className='hidden lg:block absolute h-[120%] -top-12' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 900" fill="none">
                            <path d="M12.1531 1.50019C13.2063 12.3794 31.5496 229.717 21.5021 425.002C11.4546 620.287 -1.55001 748.365 2.14142 897.696" stroke="url(#paint0_linear_259_2223)" strokeWidth="3" strokeMiterlimit="10" strokeLinecap="round"/>
                            <defs>
                                <linearGradient id="paint0_linear_259_2223" x1="12.9998" y1="897.612" x2="6.1108" y2="1.54665" gradientUnits="userSpaceOnUse">
                                    <stop offset="0.271465" stopColor="#89FF69" stopOpacity="0"/>
                                    <stop offset="0.5" stopColor="#89FF69"/>
                                    <stop offset="0.816742" stopColor="#89FF69" stopOpacity="0"/>
                                </linearGradient>
                            </defs>
                        </svg>

                        {/* Step 1 */}
                        <div className="flex items-center gap-4 w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:right-[17.5%]">
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">1</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1 flex flex-col gap-1 lg:w-[400px]">
                                <BodyXLMedium className="text-primary">Pick eligible NFTs</BodyXLMedium>
                                <BodyRegular className="text-white font-medium">Choose two (or more) compatible items</BodyRegular>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex items-center gap-4 flex-row-reverse w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:left-[17.5%]">
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">2</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1 flex flex-col gap-1 lg:w-[400px]">
                                <BodyXLMedium className="text-primary">Preview upgrade</BodyXLMedium>
                                <BodyRegular className="text-white font-medium">See new rank/benefits before confirming</BodyRegular>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex items-center gap-4 w-full max-w-[320px] lg:max-w-none lg:justify-center relative lg:right-[17.5%]">
                            <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-white/20 to-white/5 rounded-full shadow-sm backdrop-blur-xl border-2 border-white/10 flex items-center justify-center relative z-10 order-1 lg:order-2">
                                <BodyXLMedium className="text-primary">3</BodyXLMedium>
                            </div>
                            <div className="flex-1 lg:flex-none p-4 lg:p-6 bg-gradient-to-br from-white/20 to-white/5 rounded-2xl lg:rounded-3xl shadow-sm backdrop-blur-xl border-2 border-white/10 relative z-10 order-2 lg:order-1 flex flex-col gap-1 lg:w-[400px]">
                                <BodyXLMedium className="text-primary">Fuse on-chain</BodyXLMedium>
                                <BodyRegular className="text-white font-medium">Original(s) are merged, upgraded NFT is minted</BodyRegular>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Listing & Trading Flow Section */}
            <section className="w-full flex justify-center px-4 py-16">
                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col gap-6 lg:gap-12">
                    <TitleMedium className="text-white">
                        Listing & Trading <span className="text-primary">Flow</span>
                    </TitleMedium>

                    {/* Sell flow */}
                    <div className="flex flex-col gap-6">
                        <TextBadge className='w-max'>Sell</TextBadge>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-4">
                            <GenericCard
                                image={IconNFT}
                                title="Select NFT"
                                description="List for sale"
                                titleClassName="max-w-none"
                            />
                            <GenericCard
                                image={IconStartingPrice}
                                title="System suggests valid price"
                                description="≥110% of win price"
                                titleClassName="max-w-none"
                            />
                            <GenericCard
                                image={IconDuration}
                                title="Set duration"
                                description="Optional"
                                titleClassName="max-w-none"
                            />
                            <GenericCard
                                image={IconStaking}
                                title="Review fees"
                                description="Confirm listing"
                                titleClassName="max-w-none"
                            />
                        </div>
                    </div>

                    {/* Buy flow */}
                    <div className="flex flex-col gap-6">
                        <TextBadge className='w-max'>Buy</TextBadge>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-4">
                            <GenericCard
                                image={IconBrowse}
                                title="Browse filters"
                                description="rarity rank, price, time left"
                                titleClassName="max-w-none"
                            />
                            <GenericCard
                                image={IconResale}
                                title="Open Resale Record"
                                description="last sale, average, owner history"
                                titleClassName="max-w-none"
                            />
                            <GenericCard
                                image={IconBuy}
                                title="Buy now"
                                description="or send Request-to-Buy"
                                titleClassName="max-w-none"
                            />
                            <GenericCard
                                image={IconEscrow}
                                title="Confirm escrow"
                                description="receive NFT"
                                titleClassName="max-w-none"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Tutorials & Guides Section */}
            <section className="w-full flex justify-center px-4 py-8 lg:py-16 relative">
                {/* Glow effect */}
                <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-96 h-96 opacity-60 bg-cyan-400 rounded-full blur-[282.13px] pointer-events-none" />

                <div className="w-full max-w-[320px] lg:max-w-[1200px] flex flex-col gap-6 lg:gap-8 relative z-10">
                    <TitleMedium className="text-white">
                        Tutorials & <span className="text-primary">Guides</span>
                    </TitleMedium>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-4">
                        <GenericCard
                            image={IconNFT}
                            title="How to list an NFT"
                            buttonText="Learn more"
                            titleClassName="lg:max-w-[110px]"
                        />
                        <GenericCard
                            image={IconBuy}
                            title="How to buy & verify history"
                            buttonText="Learn more"
                            titleClassName="lg:max-w-[154px]"
                        />
                        <GenericCard
                            image={IconTwoNFTs}
                            title="How to fuse two NFTs"
                            buttonText="Learn more"
                            titleClassName="lg:max-w-[124px]"
                        />
                        <GenericCard
                            image={IconBrowse}
                            title="Understanding the ≥110% floor"
                            buttonText="Learn more"
                            titleClassName="lg:max-w-[154px]"
                            imageClass="-scale-x-100"
                        />
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="w-full flex justify-center px-4 py-16">
                <div className="w-full max-w-[320px] lg:max-w-[800px] flex flex-col items-center gap-6 lg:gap-8">
                    <TitleMedium className="text-white">FAQ</TitleMedium>

                    <div className="w-full flex flex-col gap-2">
                        <Accordion
                            question="What is the ≥110% floor?"
                            answer="Listings must be at least 110% of the original win price inside this marketplace."
                            defaultOpen={true}
                            className="w-full"
                        />
                        <Accordion
                            question="Can I sell externally?"
                            answer="Yes, you can sell on external marketplaces, but the 110% floor only applies within our platform."
                            className="w-full"
                        />
                        <Accordion
                            question="Do I pay fees when fusing?"
                            answer="There is a small gas fee for the fusion transaction, but no platform fees."
                            className="w-full"
                        />
                        <Accordion
                            question="Can I undo a fusion?"
                            answer="No, fusion is permanent. The original NFTs are burned and cannot be recovered."
                            className="w-full"
                        />
                    </div>

                    <PrimaryButton className="w-64">Whitepaper</PrimaryButton>
                </div>
            </section>
        </div>
    );
};